import { AutoComplete, Button, Col, Row, Select, Table, Tabs, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { PackageForImportDto } from '../../../types';
import { TrainingProjectDashboardStore } from '../../../stores';
import { Utils } from '../../../../common/services/Utils';
import { ColumnProps } from 'antd/lib/table';
import { CloseOutlined } from '@ant-design/icons';
import _ from 'lodash';

type Props = {
    store: TrainingProjectDashboardStore;
    selectedPackages: string[];
    setSelectedPackages: (packages: string[]) => void;
};

const PackageSelectionStep: React.FC<Props> = ({ store, selectedPackages, setSelectedPackages }) => {
    const [tablePage, setTablePage] = React.useState(1);
    const [selectedTab, setSelectedTab] = React.useState('1');
    const [selectedPackagesAutocomplete, setSelectedPackagesAutocomplete] = React.useState('');
    const [selectedPackagesFilter, setSelectedPackagesFilter] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [selectedTags, setSelectedTags] = React.useState<string[]>([]);

    // Get filtered packages based on search and tag filters
    const filteredPackages = React.useMemo(() => {
        return store.availablePackages.filter(pkg => {
            const matchesSearch = !searchTerm || pkg.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => pkg.tags.includes(tag));
            return matchesSearch && matchesTags;
        });
    }, [store.availablePackages, searchTerm, selectedTags]);

    // Get packages that are currently selected
    const selectedPackagesSource = React.useMemo(() => {
        return store.availablePackages.filter(pkg => selectedPackages.includes(pkg.id));
    }, [store.availablePackages, selectedPackages]);

    // Get all unique tags from available packages
    const packageTags = React.useMemo(() => {
        const allTags = _.flatMap(store.availablePackages, pkg => pkg.tags);
        return [...new Set(allTags)].sort();
    }, [store.availablePackages]);

    // Load available packages when component mounts
    React.useEffect(() => {
        if (store.project && store.availablePackages.length === 0) {
            store.loadAvailablePackages();
        }
    }, [store, store.project]);

    React.useEffect(() => {
        setTablePage(1);
    }, [filteredPackages]);

    const onAutocompleteSearh = (value: string) => {
        setSearchTerm(value);
    };

    const onSelectedPackagesAutocompleteSearch = (value: string) => {
        setSelectedPackagesAutocomplete(value);
        setSelectedPackagesFilter(value);
    };

    const columns: ColumnProps<PackageForImportDto>[] = [
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) => Utils.safeLocaleCompare(a.name, b.name),
            render: (text: string, record: PackageForImportDto) => (
                <span data-id={`Name-${record.name}`} style={{ whiteSpace: 'pre-wrap' }}>
                    {record.name}
                </span>
            )
        },
        {
            key: 'tags',
            dataIndex: 'tags',
            title: 'Tags',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) =>
                Utils.safeLocaleCompare(a.tags?.join(', '), b.tags?.join(', ')),
            render: (__: unknown, record: PackageForImportDto) => (
                <div data-id={`Tags-${record.name}`}>
                    <Tooltip title={record.tags?.join(', ')}>{record.tags?.join(', ')}</Tooltip>
                </div>
            )
        },
        {
            key: 'uploadDate',
            dataIndex: 'uploadDate',
            title: 'Upload date',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) =>
                Utils.safeDateCompare(new Date(a.uploadDate), new Date(b.uploadDate)),
            render: (uploadDate: string, record: PackageForImportDto) => (
                <span data-id={`UploadDate-${record.name}`} data-id-timestamp={uploadDate}>
                    {Utils.formatDateStringShort(uploadDate, true)}
                </span>
            )
        },
        {
            key: 'indexDate',
            dataIndex: 'indexDate',
            title: 'Index date',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) =>
                Utils.safeDateCompare(
                    a.indexDate ? new Date(a.indexDate) : null,
                    b.indexDate ? new Date(b.indexDate) : null
                ),
            render: (__: string, record: PackageForImportDto) => (
                <span data-id={`IndexDate-${record.name}`} data-id-timestamp={record.indexDate}>
                    {Utils.formatDateStringShort(record.indexDate, true)}
                </span>
            )
        }
    ];

    const selectedItemColumns: ColumnProps<PackageForImportDto>[] = [
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) => Utils.safeLocaleCompare(a.name, b.name),
            render: (__: string, record: PackageForImportDto) => (
                <span data-id={`Selected-Name-${record.name}`} style={{ whiteSpace: 'pre-wrap' }}>
                    {record.name}
                </span>
            )
        },
        {
            key: 'tags',
            dataIndex: 'tags',
            title: 'Tags',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) =>
                Utils.safeLocaleCompare(a.tags?.join(', '), b.tags?.join(', ')),
            render: (__: string, record: PackageForImportDto) => (
                <div data-id={`Selected-Tags-${record.name}`}>
                    <Tooltip title={record.tags?.join(', ')}>{record.tags?.join(', ')}</Tooltip>
                </div>
            )
        },
        {
            key: 'uploadDate',
            dataIndex: 'uploadDate',
            title: 'Upload date',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) =>
                Utils.safeDateCompare(new Date(a.uploadDate), new Date(b.uploadDate)),
            render: (__: string, record: PackageForImportDto) => (
                <span data-id={`Selected-UploadDate-${record.name}`} data-id-timestamp={record.uploadDate}>
                    {Utils.formatDateStringShort(record.uploadDate, true)}
                </span>
            )
        },
        {
            key: 'indexDate',
            dataIndex: 'indexDate',
            title: 'Index date',
            sorter: (a: PackageForImportDto, b: PackageForImportDto) =>
                Utils.safeDateCompare(
                    a.indexDate ? new Date(a.indexDate) : null,
                    b.indexDate ? new Date(b.indexDate) : null
                ),
            render: (__: string, record: PackageForImportDto) => (
                <span data-id={`Selected-IndexDate-${record.name}`} data-id-timestamp={record.indexDate}>
                    {Utils.formatDateStringShort(record.indexDate, true)}
                </span>
            )
        },
        {
            key: 'actions',
            title: '',
            width: 64,
            render: (__: string, record: PackageForImportDto) => (
                <Button
                    key={record.id + '-delete'}
                    type="link"
                    icon={<CloseOutlined />}
                    onClick={() => handleSelection(record.id, false)}
                />
            )
        }
    ];

    const handleSelection = (pkgId: string, selected: boolean) => {
        if (selected) {
            setSelectedPackages([...selectedPackages, pkgId]);
        } else {
            setSelectedPackages(selectedPackages.filter(p => p !== pkgId));
        }
    };

    const handleMultipleSelection = (
        selected: boolean,
        selectedRows: PackageForImportDto[],
        changeRows: PackageForImportDto[]
    ) => {
        if (selected) {
            setSelectedPackages([...selectedPackages, ...changeRows.map(r => r.id)]);
        } else {
            setSelectedPackages(selectedPackages.filter(p => !changeRows.map(r => r.id).includes(p)));
        }
    };

    return (
        <div style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Tabs defaultActiveKey="1" activeKey={selectedTab} onChange={setSelectedTab}>
                <Tabs.TabPane tab="Search packages" key="1">
                    <Row gutter={16}>
                        <Col span={12}>
                            <AutoComplete
                                style={{ width: '100%' }}
                                placeholder="Search for packages"
                                value={searchTerm}
                                onSearch={setSearchTerm}
                                onSelect={onAutocompleteSearh}
                                onClear={() => onAutocompleteSearh('')}
                                allowClear
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        onAutocompleteSearh(searchTerm);
                                    }
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            <Select
                                options={packageTags.map(t => ({ value: t }))}
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder="Filter by tags"
                                value={selectedTags}
                                onChange={(value: string[]) => setSelectedTags(value)}
                            />
                        </Col>
                    </Row>
                    <br />
                    <Table
                        scroll={{ y: 'calc(100vh - 500px)' }}
                        data-id="import-packages-list"
                        className="alpha-portal-table"
                        dataSource={filteredPackages}
                        loading={store.isLoadingImportOperations}
                        columns={columns}
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: false,
                            hideOnSinglePage: true,
                            showLessItems: true,
                            onChange: setTablePage,
                            current: tablePage
                        }}
                        rowKey={(record: PackageForImportDto) => record.id}
                        rowSelection={{
                            selectedRowKeys: selectedPackages,
                            onSelect: (record: PackageForImportDto, selected: boolean) =>
                                handleSelection(record.id, selected),
                            onSelectMultiple: handleMultipleSelection,
                            onSelectAll: handleMultipleSelection,
                            columnWidth: 48
                        }}
                    />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Selected packages" key="2">
                    <Row gutter={16}>
                        <Col span={12}>
                            <AutoComplete
                                style={{ width: '100%' }}
                                options={selectedPackagesSource
                                    .filter(s =>
                                        s.name.toLowerCase().includes(selectedPackagesAutocomplete.toLowerCase())
                                    )
                                    .map(source => ({
                                        value: source.name,
                                        label: <span style={{ whiteSpace: 'pre' }}>{source.name}</span>
                                    }))}
                                value={selectedPackagesAutocomplete}
                                onSearch={setSelectedPackagesAutocomplete}
                                placeholder="Search for packages"
                                onSelect={onSelectedPackagesAutocompleteSearch}
                                onClear={() => onSelectedPackagesAutocompleteSearch('')}
                                allowClear
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        onSelectedPackagesAutocompleteSearch(selectedPackagesAutocomplete);
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                    <br />
                    <Table
                        scroll={{ y: 'calc(100vh - 500px)' }}
                        data-id="import-selected-packages-list"
                        className="alpha-portal-table"
                        dataSource={
                            selectedPackagesFilter
                                ? selectedPackagesSource.filter(s =>
                                      s.name.toLowerCase().includes(selectedPackagesFilter.toLowerCase())
                                  )
                                : selectedPackagesSource
                        }
                        loading={store.isLoadingImportOperations}
                        columns={selectedItemColumns}
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: false,
                            hideOnSinglePage: true,
                            showLessItems: true,
                            onChange: setTablePage,
                            current: tablePage
                        }}
                        rowKey={(record: PackageForImportDto) => record.id}
                    />
                </Tabs.TabPane>
            </Tabs>
        </div>
    );
};

export default observer(PackageSelectionStep);
